// Token types for DSP language
export enum TokenType {
  // Literals
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  IDENTIFIER = 'IDENTIFIER',

  // Operators
  ASSIGN = 'ASSIGN', // =
  PLUS_ASSIGN = 'PLUS_ASSIGN', // +=
  MINUS_ASSIGN = 'MINUS_ASSIGN', // -=
  MULTIPLY_ASSIGN = 'MULTIPLY_ASSIGN', // *=
  DIVIDE_ASSIGN = 'DIVIDE_ASSIGN', // /=
  POWER_ASSIGN = 'POWER_ASSIGN', // ^=
  PIPE = 'PIPE', // |>
  ARROW = 'ARROW', // ->
  COLON = 'COLON', // :
  COMMA = 'COMMA', // ,
  DOT = 'DOT', // .
  PLUS = 'PLUS', // +
  MINUS = 'MINUS', // -
  MULTIPLY = 'MULTIPLY', // *
  DIVIDE = 'DIVIDE', // /
  POWER = 'POWER', // ^
  UNIPOLAR = 'UNIPOLAR', // ^^
  PERCENT = 'PERCENT', // %
  AT = 'AT', // @

  // Delimiters
  LPAREN = 'LPAREN', // (
  RPAREN = 'RPAREN', // )
  LBRACE = 'LBRACE', // {
  RBRACE = 'RBRACE', // }
  LBRACKET = 'LBRACKET', // [
  RBRACKET = 'RBRACKET', // ]

  // Keywords
  RETURN = 'RETURN',

  // Special
  UNDERSCORE = 'UNDERSCORE', // _
  NEWLINE = 'NEWLINE',
  EOF = 'EOF',
}

export interface Token {
  type: TokenType
  value: string
  line: number
  column: number
  quoteType?: '\'' | '"' // Store quote type for strings
}

export class Lexer {
  private source: string
  private position = 0
  private line = 1
  private column = 1

  constructor(source: string) {
    this.source = source
  }

  tokenize(): Token[] {
    const tokens: Token[] = []

    while (this.position < this.source.length) {
      const token = this.nextToken()
      if (token) {
        tokens.push(token)
      }
    }

    tokens.push(this.createToken(TokenType.EOF, ''))
    return tokens
  }

  private nextToken(): Token | null {
    this.skipWhitespaceExceptNewline()

    if (this.position >= this.source.length) {
      return null
    }

    const char = this.source[this.position]

    // Comments
    if (char === '/' && this.peek() === '/') {
      this.skipComment()
      return this.nextToken()
    }

    // Newline
    if (char === '\n') {
      const token = this.createToken(TokenType.NEWLINE, '\n')
      this.advance()
      return token
    }

    // Numbers (including fractions like 1/8)
    if (this.isDigit(char) || (char === '.' && this.isDigit(this.peek()))) {
      return this.readNumber()
    }

    // Strings
    if (char === '\'' || char === '"') {
      return this.readString()
    }

    // Identifiers and keywords
    if (this.isAlpha(char) || char === '_') {
      return this.readIdentifier()
    }

    // Operators and delimiters
    switch (char) {
      case '=':
        return this.singleChar(TokenType.ASSIGN)
      case ':':
        return this.singleChar(TokenType.COLON)
      case ',':
        return this.singleChar(TokenType.COMMA)
      case '.':
        return this.singleChar(TokenType.DOT)
      case '+':
        if (this.peek() === '=') {
          const token = this.createToken(TokenType.PLUS_ASSIGN, '+=')
          this.advance()
          this.advance()
          return token
        }
        return this.singleChar(TokenType.PLUS)
      case '-':
        if (this.peek() === '>') {
          const token = this.createToken(TokenType.ARROW, '->')
          this.advance()
          this.advance()
          return token
        }
        if (this.peek() === '=') {
          const token = this.createToken(TokenType.MINUS_ASSIGN, '-=')
          this.advance()
          this.advance()
          return token
        }
        return this.singleChar(TokenType.MINUS)
      case '*':
        if (this.peek() === '=') {
          const token = this.createToken(TokenType.MULTIPLY_ASSIGN, '*=')
          this.advance()
          this.advance()
          return token
        }
        return this.singleChar(TokenType.MULTIPLY)
      case '/':
        if (this.peek() === '=') {
          const token = this.createToken(TokenType.DIVIDE_ASSIGN, '/=')
          this.advance()
          this.advance()
          return token
        }
        return this.singleChar(TokenType.DIVIDE)
      case '^':
        if (this.peek() === '=') {
          const token = this.createToken(TokenType.POWER_ASSIGN, '^=')
          this.advance()
          this.advance()
          return token
        }
        if (this.peek() === '^') {
          const token = this.createToken(TokenType.UNIPOLAR, '^^')
          this.advance()
          this.advance()
          return token
        }
        return this.singleChar(TokenType.POWER)
      case '%':
        return this.singleChar(TokenType.PERCENT)
      case '@':
        return this.singleChar(TokenType.AT)
      case '(':
        return this.singleChar(TokenType.LPAREN)
      case ')':
        return this.singleChar(TokenType.RPAREN)
      case '{':
        return this.singleChar(TokenType.LBRACE)
      case '}':
        return this.singleChar(TokenType.RBRACE)
      case '[':
        return this.singleChar(TokenType.LBRACKET)
      case ']':
        return this.singleChar(TokenType.RBRACKET)
      case '|':
        if (this.peek() === '>') {
          const token = this.createToken(TokenType.PIPE, '|>')
          this.advance()
          this.advance()
          return token
        }
        break
    }

    throw new Error(`Unexpected character '${char}' at line ${this.line}, column ${this.column}`)
  }

  private readNumber(): Token {
    const start = this.position
    const startLine = this.line
    const startColumn = this.column

    // Handle leading dot case (.15)
    if (this.current() === '.' && this.isDigit(this.peek())) {
      this.advance() // skip '.'
      // Read fractional digits
      while (this.isDigit(this.current())) {
        this.advance()
      }
      const value = this.source.slice(start, this.position)
      return { type: TokenType.NUMBER, value, line: startLine, column: startColumn }
    }

    // Read integer part
    while (this.isDigit(this.current())) {
      this.advance()
    }

    // Read decimal part - allow trailing dot (1.)
    if (this.current() === '.') {
      const nextChar = this.peek()
      // Accept dot if followed by digit OR if we already read digits (trailing dot like "1.")
      if (this.isDigit(nextChar) || (this.position > start)) {
        this.advance() // skip '.'
        // Read fractional digits if present
        while (this.isDigit(this.current())) {
          this.advance()
        }
      }
    }

    const value = this.source.slice(start, this.position)
    return { type: TokenType.NUMBER, value, line: startLine, column: startColumn }
  }

  private readString(): Token {
    const startLine = this.line
    const startColumn = this.column
    const quote = this.current()
    this.advance() // skip opening quote

    let value = ''
    let loopCount = 0
    const maxLoops = 10000 // Safety limit for multiline strings

    while (this.position < this.source.length && loopCount < maxLoops) {
      loopCount++
      const current = this.current()

      // Check for closing quote
      if (current === quote) {
        this.advance() // skip closing quote
        return {
          type: TokenType.STRING,
          value,
          line: startLine,
          column: startColumn,
          quoteType: quote as '\'' | '"',
        }
      }

      // Add character (including newlines for multiline support)
      value += current
      this.advance()
    }

    // Unterminated string
    throw new Error(`Unterminated string at line ${startLine}, column ${startColumn}`)
  }

  private readIdentifier(): Token {
    const start = this.position
    const startLine = this.line
    const startColumn = this.column

    while (this.isAlphaNumeric(this.current()) || this.current() === '_') {
      this.advance()
    }

    const value = this.source.slice(start, this.position)

    // Check for keywords
    const type = value === 'return' ? TokenType.RETURN : TokenType.IDENTIFIER

    return { type, value, line: startLine, column: startColumn }
  }

  private singleChar(type: TokenType): Token {
    const token = this.createToken(type, this.current())
    this.advance()
    return token
  }

  private skipWhitespaceExceptNewline() {
    while (this.position < this.source.length) {
      const char = this.current()
      if (char === ' ' || char === '\t' || char === '\r') {
        this.advance()
      }
      else {
        break
      }
    }
  }

  private skipComment() {
    while (this.current() !== '\n' && this.position < this.source.length) {
      this.advance()
    }
  }

  private isDigit(char: string): boolean {
    return char >= '0' && char <= '9'
  }

  private isAlpha(char: string): boolean {
    return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')
  }

  private isAlphaNumeric(char: string): boolean {
    return this.isAlpha(char) || this.isDigit(char)
  }

  private current(): string {
    return this.source[this.position] || ''
  }

  private peek(): string {
    return this.source[this.position + 1] || ''
  }

  private advance() {
    if (this.source[this.position] === '\n') {
      this.line++
      this.column = 1
    }
    else {
      this.column++
    }
    this.position++
  }

  private createToken(type: TokenType, value: string): Token {
    return { type, value, line: this.line, column: this.column }
  }
}
